import { useTranslation } from 'react-i18next';
import { PublicShell } from '../components/PublicShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';
import { HomeHero } from '../components/public/home/HomeHero';
import { HomeBrowserStrip } from '../components/public/home/HomeBrowserStrip';
import { HomeStats } from '../components/public/home/HomeStats';
import { HomeControlCenter } from '../components/public/home/HomeControlCenter';
import { HomeEssentials } from '../components/public/home/HomeEssentials';
import { HomeFinalCta } from '../components/public/home/HomeFinalCta';

const ROUTE = '/';
const OG_IMAGE = 'https://markflow.fyi/og.png';
const LOGO_URL = 'https://markflow.fyi/web-app-manifest-512x512.png';

// Public marketing homepage shown at `/` for logged-out visitors (App routes
// authed users to the bookmark app instead). Owns its own <main> so each
// section controls its own width/spacing.
export function HomeLandingPage() {
  const { t, i18n } = useTranslation();
  const lang = activeLang(i18n.resolvedLanguage);
  const pageUrl = publicUrl(ROUTE, lang);

  usePageMeta({
    title: t('home.meta.title'),
    description: t('home.meta.description'),
    canonical: pageUrl,
    ogTitle: t('home.meta.ogTitle'),
    ogDescription: t('home.meta.ogDescription'),
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(ROUTE),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'MarkFlow',
        description: t('home.meta.ogDescription'),
        applicationCategory: 'ProductivityApplication',
        operatingSystem: 'Web, Chrome, Firefox, Edge, Opera',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
        },
        url: pageUrl,
        image: OG_IMAGE,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'MarkFlow',
        url: publicUrl('/', 'en'),
        logo: LOGO_URL,
      },
    ],
  });

  return (
    <PublicShell headerVariant="transparent" bgClassName="landing-bg">
      <main className="landing-root flex-1">
        <HomeHero />
        <HomeBrowserStrip />
        <HomeStats />
        <HomeControlCenter />
        <HomeEssentials />
        <HomeFinalCta />
      </main>
    </PublicShell>
  );
}
